/**
 * StatsPanel - Manages the stats panel UI (right side panel)
 * Refactored to use class-based architecture
 */

import Phaser from "phaser";
import { GlobalState } from "../systems/GlobalState";
import { UI_COLORS } from "../constants/gameConstants";
import { GAME_CONFIG } from "../config";

/**
 * Configuration for creating stats panel
 */
export type StatsPanelConfig = {
  viewportWidth: number;
  viewportHeight: number;
  panelWidth: number;
  levelNumber: number | string;
  starsRemaining: number;
  isCustomLevel: boolean;
  onRestart: () => void;
  levelWidth: number;
  levelHeight: number;
  globalState: GlobalState | null;
};

/**
 * StatsPanel class - manages the right-side stats panel UI
 */
export class StatsPanel {
  private scene: Phaser.Scene;
  private globalState: GlobalState | null;

  // Container & layout
  private container: Phaser.GameObjects.Container;
  private panelX: number;
  private panelCenterX: number;
  private panelWidth: number;
  private rectangleWidth: number;
  private levelWidth: number;
  private levelHeight: number;
  private viewportHeight: number;

  // Text displays
  private levelText: Phaser.GameObjects.Text | null = null;
  private counterText: Phaser.GameObjects.Text | null = null;

  // Section labels & backgrounds
  private levelLabel: Phaser.GameObjects.Text | null = null;
  private levelNumBg: Phaser.GameObjects.Rectangle | null = null;
  private starsLabel: Phaser.GameObjects.Text | null = null;
  private starsBg: Phaser.GameObjects.Rectangle | null = null;
  private itemsLabel: Phaser.GameObjects.Text | null = null;
  private itemsBg: Phaser.GameObjects.Rectangle | null = null;

  // Hint section
  private hintLabel: Phaser.GameObjects.Text | null = null;
  private hintBg: Phaser.GameObjects.Rectangle | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;

  // Buttons (public for external event binding)
  public debugButton: Phaser.GameObjects.Text | null = null;
  public copyButton: Phaser.GameObjects.Text | null = null;

  // Item sprites
  private itemGridSprites: Phaser.GameObjects.Sprite[] = [];
  private itemsBgCenterY: number = 0;

  constructor(scene: Phaser.Scene, config: StatsPanelConfig) {
    this.scene = scene;
    this.globalState = config.globalState;
    this.levelWidth = config.levelWidth;
    this.levelHeight = config.levelHeight;
    this.viewportHeight = config.viewportHeight;

    // Calculate layout dimensions
    this.panelX = config.viewportWidth;
    this.panelCenterX = this.panelX + config.panelWidth / 2 + 3;
    this.panelWidth = config.panelWidth;
    this.rectangleWidth = config.panelWidth - 40;

    // Create container
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(100);

    // Create all UI sections
    this.createPanelBackground(config.viewportHeight);

    let yPos = 20;
    yPos = this.createLevelSection(yPos, config.levelNumber, config.isCustomLevel);
    this.createHintSection();
    yPos = this.createStarsSection(yPos, config.starsRemaining);
    yPos = this.createItemsSection(yPos);
    this.createControlButtons(config.viewportHeight, config.onRestart);
  }

  /**
   * Create panel background and border
   */
  private createPanelBackground(viewportHeight: number): void {
    // Stats panel background (Mario brick/gold style)
    const panelBg = this.scene.add.rectangle(
      this.panelCenterX,
      viewportHeight / 2,
      this.panelWidth,
      viewportHeight,
      UI_COLORS.brickBrown
    );
    panelBg.setScrollFactor(0);
    this.container.add(panelBg);

    // Panel border (left edge)
    const panelBorder = this.scene.add.rectangle(
      this.panelX + 2,
      viewportHeight / 2,
      4,
      viewportHeight,
      0x000000
    );
    panelBorder.setScrollFactor(0);
    this.container.add(panelBorder);
  }

  /**
   * Create LEVEL section
   * Returns next Y position
   */
  private createLevelSection(yPos: number, levelNumber: number | string, isCustomLevel: boolean): number {
    // LEVEL label
    this.levelLabel = this.scene.add.text(this.panelCenterX, yPos, "LEVEL", {
      fontSize: "14px",
      color: "#ffff00",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 2,
    });
    this.levelLabel.setOrigin(0.5, 0);
    this.levelLabel.setScrollFactor(0);
    this.container.add(this.levelLabel);

    yPos += 20;

    // Level number background (golden box style)
    this.levelNumBg = this.scene.add.rectangle(
      this.panelCenterX,
      yPos + 15,
      this.rectangleWidth,
      30,
      UI_COLORS.goldBox
    );
    this.levelNumBg.setStrokeStyle(2, 0x000000);
    this.levelNumBg.setScrollFactor(0);
    this.container.add(this.levelNumBg);

    // Level number text
    const levelNum = isCustomLevel ? "?" : String(levelNumber);
    this.levelText = this.scene.add.text(this.panelCenterX, yPos + 15, levelNum, {
      fontSize: "20px",
      color: `#${UI_COLORS.goldText.toString(16).padStart(6, "0")}`,
      fontFamily: "Arial",
      fontStyle: "bold",
    });
    this.levelText.setOrigin(0.5);
    this.levelText.setScrollFactor(0);
    this.container.add(this.levelText);

    return yPos + 50;
  }

  /**
   * Create HINT section (hidden by default)
   */
  private createHintSection(): void {
    // HINT label (positioned same as LEVEL, hidden by default)
    this.hintLabel = this.scene.add.text(this.panelCenterX, 20, "HINT", {
      fontSize: "14px",
      color: "#ffff00",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 2,
    });
    this.hintLabel.setOrigin(0.5, 0);
    this.hintLabel.setScrollFactor(0);
    this.hintLabel.setAlpha(0);
    this.container.add(this.hintLabel);

    // Hint background
    this.hintBg = this.scene.add.rectangle(
      this.panelCenterX,
      55,
      this.rectangleWidth,
      180,
      UI_COLORS.goldBox
    );
    this.hintBg.setStrokeStyle(2, 0x000000);
    this.hintBg.setScrollFactor(0);
    this.hintBg.setAlpha(0);
    this.container.add(this.hintBg);

    // Hint text
    this.hintText = this.scene.add.text(this.panelCenterX, 45, "", {
      fontSize: "16px",
      color: `#fff000`,
      fontFamily: "Arial",
      fontStyle: "italic",
      wordWrap: { width: this.panelWidth - 30 },
      align: "center",
    });
    this.hintText.setOrigin(0.5, 0);
    this.hintText.setScrollFactor(0);
    this.hintText.setAlpha(0);
    this.container.add(this.hintText);
  }

  /**
   * Create STARS LEFT section
   * Returns next Y position
   */
  private createStarsSection(yPos: number, starsRemaining: number): number {
    // STARS LEFT label
    this.starsLabel = this.scene.add.text(this.panelCenterX, yPos, "STARS LEFT", {
      fontSize: "14px",
      color: "#ffff00",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 2,
    });
    this.starsLabel.setOrigin(0.5, 0);
    this.starsLabel.setScrollFactor(0);
    this.container.add(this.starsLabel);

    yPos += 20;

    // Stars count background (golden box style)
    this.starsBg = this.scene.add.rectangle(
      this.panelCenterX,
      yPos + 15,
      this.rectangleWidth,
      30,
      UI_COLORS.goldBox
    );
    this.starsBg.setStrokeStyle(2, 0x000000);
    this.starsBg.setScrollFactor(0);
    this.container.add(this.starsBg);

    // Stars counter text
    this.counterText = this.scene.add.text(
      this.panelCenterX,
      yPos + 15,
      String(starsRemaining),
      {
        fontSize: "20px",
        color: `#${UI_COLORS.goldText.toString(16).padStart(6, "0")}`,
        fontFamily: "Arial",
        fontStyle: "bold",
      }
    );
    this.counterText.setOrigin(0.5);
    this.counterText.setScrollFactor(0);
    this.container.add(this.counterText);

    return yPos + 55;
  }

  /**
   * Create ITEMS section
   * Returns next Y position
   */
  private createItemsSection(yPos: number): number {
    // ITEMS label
    this.itemsLabel = this.scene.add.text(this.panelCenterX, yPos, "ITEMS", {
      fontSize: "14px",
      color: "#ffff00",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 2,
    });
    this.itemsLabel.setOrigin(0.5, 0);
    this.itemsLabel.setScrollFactor(0);
    this.container.add(this.itemsLabel);

    yPos += 25;

    // Items container background (golden box style) - 3x2 grid (3 rows, 2 columns)
    const itemsBgHeight = 160;
    this.itemsBgCenterY = yPos + itemsBgHeight / 2;
    this.itemsBg = this.scene.add.rectangle(
      this.panelCenterX,
      this.itemsBgCenterY,
      this.rectangleWidth,
      itemsBgHeight,
      UI_COLORS.goldBox
    );
    this.itemsBg.setStrokeStyle(2, 0x000000);
    this.itemsBg.setScrollFactor(0);
    this.container.add(this.itemsBg);

    return yPos + itemsBgHeight + 10;
  }

  /**
   * Create control buttons (debug, copy, restart)
   */
  private createControlButtons(viewportHeight: number, onRestart: () => void): void {
    const viewportWidth = this.panelX;

    // Debug button (bug icon)
    this.debugButton = this.scene.add.text(
      viewportWidth + this.rectangleWidth,
      viewportHeight - 20,
      "🐛",
      {
        fontSize: "14px",
        color: "#000000",
        fontFamily: "Arial",
        fontStyle: "bold",
      }
    );
    this.debugButton.setOrigin(0.5);
    this.debugButton.setScrollFactor(0);
    this.debugButton.setInteractive({ useHandCursor: true });
    this.container.add(this.debugButton);

    // Copy button (clipboard icon)
    this.copyButton = this.scene.add.text(
      viewportWidth + this.rectangleWidth - 25,
      viewportHeight - 20,
      "📋",
      {
        fontSize: "14px",
        color: "#000000",
        fontFamily: "Arial",
        fontStyle: "bold",
      }
    );
    this.copyButton.setOrigin(0.5);
    this.copyButton.setScrollFactor(0);
    this.copyButton.setInteractive({ useHandCursor: true });
    this.container.add(this.copyButton);

    // Restart button
    const restartButton = this.scene.add.rectangle(
      this.panelCenterX,
      viewportHeight - 55,
      this.rectangleWidth,
      36,
      UI_COLORS.greenButton
    );
    restartButton.setStrokeStyle(2, 0x000000);
    restartButton.setScrollFactor(0);
    restartButton.setInteractive({ useHandCursor: true });
    this.container.add(restartButton);

    const restartText = this.scene.add.text(
      this.panelCenterX,
      viewportHeight - 55,
      "RESTART",
      {
        fontSize: "14px",
        color: "#ffffff",
        fontFamily: "Arial",
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 2,
      }
    );
    restartText.setOrigin(0.5);
    restartText.setScrollFactor(0);
    this.container.add(restartText);

    // Restart button hover effects and click handler
    restartButton.on("pointerover", () => {
      restartButton.setFillStyle(UI_COLORS.greenButtonHover);
    });

    restartButton.on("pointerout", () => {
      restartButton.setFillStyle(UI_COLORS.greenButton);
    });

    restartButton.on("pointerdown", onRestart);
  }

  /**
   * Update stats display from global state (call every frame)
   */
  public update(): void {
    if (!this.globalState || !this.counterText) return;

    // Update stars counter
    const remaining = this.globalState.totalStars - this.globalState.starsCollected;
    this.counterText.setText(String(remaining));

    // Get collected items from global state
    const collectedItems = this.globalState.getCollectedItems();

    // Destroy old sprites
    this.itemGridSprites.forEach(sprite => sprite.destroy());
    this.itemGridSprites = [];

    // Create sprites in 3x2 grid (3 rows, 2 columns)
    const gridCols = 2;
    const cellWidth = 50;  // horizontal spacing
    const cellHeight = 50; // vertical spacing
    const spriteScale = 32 / 42; // Scale down from 42x42 to 32x32

    // Calculate top-left corner of grid
    const gridStartX = this.panelCenterX - (cellWidth / 2);
    const gridStartY = this.itemsBgCenterY - cellHeight;

    collectedItems.forEach((item, index) => {
      const row = Math.floor(index / gridCols);
      const col = index % gridCols;

      const x = gridStartX + (col * cellWidth);
      const y = gridStartY + (row * cellHeight);

      const sprite = this.scene.add.sprite(x, y, item.texture);
      sprite.setScale(spriteScale);
      sprite.setScrollFactor(0);
      sprite.setDepth(100);

      this.itemGridSprites.push(sprite);
      this.container.add(sprite);
    });
  }

  /**
   * Show hint text in stats panel (hides other stats content)
   */
  public showHint(hintTextContent: string): void {
    if (!this.hintLabel || !this.hintBg || !this.hintText) return;

    // Hide all stats panel content
    this.hideContent();

    // Set hint text content
    this.hintText.setText(hintTextContent);

    // Calculate height based on text bounds
    const textBounds = this.hintText.getBounds();
    const textHeight = textBounds.height;
    const boxHeight = Math.max(80, textHeight + 20);

    // Position box and text
    this.hintBg.setY(40 + boxHeight / 2);
    this.hintText.setY(40 + 10);

    // Show hint elements
    this.hintLabel.setAlpha(1);
    this.hintBg.setAlpha(0);
    this.hintText.setAlpha(1);
  }

  /**
   * Hide hint text from stats panel (restores stats content)
   */
  public hideHint(): void {
    if (!this.hintLabel || !this.hintBg || !this.hintText) return;

    // Hide hint elements
    this.hintLabel.setAlpha(0);
    this.hintBg.setAlpha(0);
    this.hintText.setAlpha(0);

    // Restore stats panel content
    this.showContent();
  }

  /**
   * Hide all stats panel content (for debug mode)
   */
  public hideContent(): void {
    // Hide level section
    if (this.levelLabel) this.levelLabel.setAlpha(0);
    if (this.levelNumBg) this.levelNumBg.setAlpha(0);
    if (this.levelText) this.levelText.setAlpha(0);

    // Hide stars section
    if (this.starsLabel) this.starsLabel.setAlpha(0);
    if (this.starsBg) this.starsBg.setAlpha(0);
    if (this.counterText) this.counterText.setAlpha(0);

    // Hide items section
    if (this.itemsLabel) this.itemsLabel.setAlpha(0);
    if (this.itemsBg) this.itemsBg.setAlpha(0);
    this.itemGridSprites.forEach((sprite) => sprite.setAlpha(0));

    // Hide hint elements
    if (this.hintLabel) this.hintLabel.setAlpha(0);
    if (this.hintBg) this.hintBg.setAlpha(0);
    if (this.hintText) this.hintText.setAlpha(0);
  }

  /**
   * Show stats panel content (restore from debug mode)
   */
  public showContent(): void {
    // Show level section
    if (this.levelLabel) this.levelLabel.setAlpha(1);
    if (this.levelNumBg) this.levelNumBg.setAlpha(1);
    if (this.levelText) this.levelText.setAlpha(1);

    // Show stars section
    if (this.starsLabel) this.starsLabel.setAlpha(1);
    if (this.starsBg) this.starsBg.setAlpha(1);
    if (this.counterText) this.counterText.setAlpha(1);

    // Show items section
    if (this.itemsLabel) this.itemsLabel.setAlpha(1);
    if (this.itemsBg) this.itemsBg.setAlpha(1);
    this.itemGridSprites.forEach((sprite) => sprite.setAlpha(1));
  }

  /**
   * Destroy stats panel and cleanup
   */
  public destroy(): void {
    this.container.destroy();
    this.itemGridSprites = [];
    this.levelText = null;
    this.counterText = null;
    this.levelLabel = null;
    this.levelNumBg = null;
    this.starsLabel = null;
    this.starsBg = null;
    this.itemsLabel = null;
    this.itemsBg = null;
    this.hintLabel = null;
    this.hintBg = null;
    this.hintText = null;
    this.debugButton = null;
    this.copyButton = null;
  }
}
